import { numberFormatter } from "@common/helpers/number";
import { Button } from "@design-system/Button/Button";
import {
    CellText,
    type DataGridCellProps,
    useCellControlTabIndex,
} from "@design-system/DataGrid";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { PlusIcon } from "@icons";
import type { NestedTaskRow } from "../../types";
import styles from "./TaskGroupLabel.module.scss";

/**
 * A group row's label in the Task column (its `summary`): the title, the
 * count of its tasks without drafts (hidden when it has none), and "+" to
 * expand it and create a draft task. The tree column draws the chevron
 * before it.
 */
export const TaskGroupLabel = ({
    context,
}: DataGridCellProps<NestedTaskRow>) => {
    const { row } = context;
    const { title = "", titleNode, tasks, onAddPlaceholder } = row.original;
    const tabIndex = useCellControlTabIndex();
    const taskCount = tasks.filter((task) => !task.isNewPlaceholder).length;
    const createLabel = `Add ${title} task`;

    return (
        <span className={styles.groupLabel}>
            {titleNode ?? <CellText text={title} overflow="truncate" />}
            {taskCount > 0 ? (
                <span className={styles.count}>
                    {numberFormatter.format(taskCount)}
                </span>
            ) : null}
            {onAddPlaceholder ? (
                <Tooltip content={createLabel}>
                    <Button
                        variant="secondary"
                        size="sm"
                        iconOnly
                        aria-label={createLabel}
                        tabIndex={tabIndex}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                            if (!row.getIsExpanded()) {
                                row.toggleExpanded();
                            }
                            onAddPlaceholder(row.id);
                        }}
                    >
                        <PlusIcon />
                    </Button>
                </Tooltip>
            ) : null}
        </span>
    );
};
